import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import ParallaxSection from "./ParallaxSection";

// Replace these with verified client quotes, photos, and permissions when available.
const testimonials = [
  {
    id: 1,
    name: "Ministry Leader",
    title: "London, United Kingdom",
    quote:
      "Wisdom’s teaching helped our leadership team navigate a difficult season with both faith and practical strategy. The books and sessions go far beyond theory.",
    image: "https://placehold.co/100x100/3D405B/fff?text=ML&font=Montserrat",
    rating: 5,
  },
  {
    id: 2,
    name: "Entrepreneur",
    title: "Business Founder",
    quote:
      "Mentorship with Wisdom Peters brought clarity on purpose-aligned decisions. His writing on provision and mindset reshaped how I approach opportunity.",
    image: "https://placehold.co/100x100/81B29A/fff?text=EN&font=Montserrat",
    rating: 5,
  },
  {
    id: 3,
    name: "Executive Coach",
    title: "Manchester, United Kingdom",
    quote:
      "The blend of psychology, faith, and leadership in Wisdom’s courses is rare. I’ve recommended his materials to clients seeking spiritual grounding and practical tools.",
    image: "https://placehold.co/100x100/E07A5F/fff?text=EC&font=Montserrat",
    rating: 5,
  },
  {
    id: 4,
    name: "Reader & Ministry Worker",
    title: "Faith Community",
    quote:
      "Every Wisdom Peters book I’ve read has challenged me to grow emotionally and spiritually — clear teaching, deep insight, and a heart for transformation.",
    image: "https://placehold.co/100x100/F2CC8F/333?text=RW&font=Montserrat",
    rating: 5,
  },
];

const TestimonialCard = ({ testimonial, isActive }) => (
  <div
    className={`transition-all duration-500 transform bg-white/5 backdrop-blur-md border border-white/10 shadow-lg p-6 rounded-lg ${
      isActive ? "scale-100 opacity-100 z-10" : "scale-90 opacity-50"
    }`}
  >
    <div className="flex items-center gap-2 mb-4">
      {Array.from({ length: testimonial.rating }).map((_, index) => (
        <Star key={index} className="w-4 h-4 text-yellow-400" />
      ))}
    </div>
    <p className="text-gray-200 text-sm italic mb-4">
      &ldquo;{testimonial.quote}&rdquo;
    </p>
    <div className="flex items-center gap-4">
      <img
        src={testimonial.image}
        alt={testimonial.name}
        className="w-12 h-12 rounded-full object-cover"
      />
      <div>
        <h4 className="text-white font-semibold">{testimonial.name}</h4>
        <p className="text-gray-400 text-sm">{testimonial.title}</p>
      </div>
    </div>
  </div>
);

const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const prevSlide = () => {
    setActiveIndex(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    );
  };

  const nextSlide = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  const getSlide = (offset) => {
    return testimonials[
      (activeIndex + offset + testimonials.length) % testimonials.length
    ];
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <ParallaxSection
      image="/pl 2.jpeg"
      overlayClass="bg-gradient-to-br from-gray-900/85 via-purple-900/80 to-black/85"
      minHeight="min-h-0"
      className="py-16"
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 mb-4">
            Testimonials
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            What readers, leaders, and mentees experience with Wisdom Peters.
          </p>
        </div>

        <div className="relative flex items-center justify-center space-x-2 md:space-x-4 max-w-5xl mx-auto">
          <button
            type="button"
            onClick={prevSlide}
            className="absolute -left-4 lg:-left-6 z-20 p-2 bg-white/10 text-white hover:bg-white/20 rounded-full"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="w-4 h-4 text-gray-100" />
          </button>

          <div className="flex items-center justify-center w-full overflow-hidden">
            <div className="flex space-x-4 transition-transform duration-500">
              {[getSlide(-1), getSlide(0), getSlide(1)].map(
                (testimonial, index) => (
                  <div
                    key={`${testimonial.id}-${index}`}
                    className={`transition-all duration-500
                  ${
                    index === 1
                      ? "w-full sm:w-[55%] scale-100 z-10"
                      : "hidden sm:block sm:w-[30%] scale-95 opacity-50"
                  }
                `}
                  >
                    <TestimonialCard
                      testimonial={testimonial}
                      isActive={index === 1}
                    />
                  </div>
                )
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={nextSlide}
            className="absolute -right-6 z-20 p-2 bg-white/10 text-white hover:bg-white/20 rounded-full"
            aria-label="Next testimonial"
          >
            <ChevronRight className="w-4 h-4 text-gray-100" />
          </button>
        </div>

        <div className="flex justify-center items-center mt-8">
          <div className="flex -space-x-3">
            {testimonials.slice(0, 4).map((t) => (
              <img
                key={t.id}
                className="w-10 h-10 rounded-full border-2 border-white"
                src={t.image}
                alt={t.name}
              />
            ))}
          </div>
        </div>
      </div>
    </ParallaxSection>
  );
};

export default Testimonials;
